import { useEffect, useMemo } from "react";

import { Flex, Image } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { HEIGHT } from "app/components/Header";
import { openOngoingActivityModal } from "app/stores/useLayoutStore";
import { routes } from "config/routes";
import { useDeliveryOptions } from "flows/Inbound/hooks/useDeliveryOptions";
import { DeliveryType } from "flows/Inbound/stores/inboundStore/types";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { useSharedListCreator } from "flows/Inbound/stores/useSharedListCreator";
import DeliveryTruckColored from "images/random/delivery-truck-colored.svg";
import { Page } from "shared/components/Page";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";
import { DropDown, DropDownProps } from "ui/DropDown/DropDown";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { BodyM, HeaderS } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

function useHandleDeliverySelection() {
  const { setSelectedOption } = useInboundStore(
    useShallow((state) => ({
      setSelectedOption: state.setSelectedOption,
    })),
  );
  const navigate = useNavigate();
  const { createSharedList } = useSharedListCreator();

  return (delivery: string | null) => {
    const selectedOptionKey = delivery as DeliveryType;
    if (selectedOptionKey) {
      setSelectedOption(selectedOptionKey);
      if (
        selectedOptionKey === DeliveryType.REWE ||
        selectedOptionKey === DeliveryType.Lekkerland
      ) {
        navigate(routes.inbound.scanRolli);
      } else {
        createSharedList(selectedOptionKey);
      }
    }
  };
}

export function SelectDeliveryPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const handleDeliverySelection = useHandleDeliverySelection();
  const { selectedOption, setSelectedOption } = useInboundStore(
    useShallow((state) => ({
      selectedOption: state.deliveryType,
      setSelectedOption: state.setSelectedOption,
    })),
  );

  const timedActivityInstance = useTimedActivityStore((state) => state.timedActivityInstance);
  const selectionOptions = useDeliveryOptions();
  const dropDownProps = useMemo<DropDownProps>(() => {
    const props: DropDownProps = {
      options: selectionOptions,
      value: selectedOption,
      onInput: handleDeliverySelection,
    };
    if (isNotNullNorUndefined(timedActivityInstance)) {
      props.onClick = () => openOngoingActivityModal();
    }
    return props;
  }, [selectionOptions, selectedOption, handleDeliverySelection, timedActivityInstance]);

  useEffect(() => {
    setSelectedOption(null);
  }, [setSelectedOption]);

  return (
    <Page isFull isBgGrey h="100%">
      <NavigationHeader
        title={intl.formatMessage({
          id: "flows.inbound.inbound-pages.header",
        })}
        onClickGoBack={() => navigate(routes.inbound.root)}
      />
      <Flex
        flex={1}
        w="100%"
        h="100%"
        maxH={`calc(100% - ${HEIGHT})`}
        align="center"
        justify="center"
      >
        <Flex direction="column" align="center">
          <HeaderS mb="s100">
            <FormattedMessage id="flows.inbound.select-delivery.title" />
          </HeaderS>
          <BodyM textAlign="center">
            <FormattedMessage id="flows.inbound.select-delivery.description" />
          </BodyM>
          <Image src={DeliveryTruckColored} h="124px" my="s500" />
          <DropDown {...dropDownProps}>
            <FormattedMessage id="flows.inbound.select-delivery.selection.placeholder" />
          </DropDown>
        </Flex>
      </Flex>
    </Page>
  );
}
